import type {
  ErrorHandlerFunction,
  HandledResponse,
  InitOverrideFunction,
} from '@/api/generator/runtime';
import { getCurrentScope, onScopeDispose } from 'vue';
import { ignoreCancellationErrorHandler, AnyApiError } from '@/errorHandling';

/**
 * Shape of a generated API method as actually called here: request params, a list of
 * per-endpoint error handlers, and optional fetch overrides. `P`/`R` are inferred per call site.
 * Error handlers are typed against the app-wide `AnyApiError` contract - per-endpoint error
 * narrowing lives on the generated API methods themselves, not this composable.
 */
type ApiCall<P = unknown, R = unknown> = (
  requestParams?: P,
  errorHandlers?: ErrorHandlerFunction<AnyApiError>[],
  initOverrides?: RequestInit | InitOverrideFunction,
) => Promise<HandledResponse<R>>;

type LatestApiCallResult<R> = {
  result: R | undefined;
  success: boolean;
  raw?: Response;
};

/**
 * Wraps an API call with latest-wins semantics and exposes a small request API.
 */
export function useLatestApiRequest<P, R>({ request }: { request: ApiCall<P, R> }) {
  let latestRequestId = 0;
  let latestController: AbortController | undefined;

  function abortLatestRequest() {
    latestController?.abort();
  }

  function beginLatestRequest() {
    latestController?.abort();
    latestController = new AbortController();
    latestRequestId += 1;

    return {
      controller: latestController,
      requestId: latestRequestId,
    };
  }

  function isLatestRequest(requestId: number) {
    return latestRequestId === requestId;
  }

  if (getCurrentScope()) {
    onScopeDispose(() => {
      abortLatestRequest();
    });
  }

  function mergeSignalIntoInitOverrides(
    signal: AbortSignal,
    initOverrides?: RequestInit,
  ): RequestInit {
    if (!initOverrides) {
      return { signal };
    }

    return { ...initOverrides, signal };
  }

  /**
   * Executes request and returns only when this call is still the latest one.
   * For stale responses, returns a failed HandledResponse-like shape.
   */
  async function call(
    requestParams: P,
    errorHandlers?: ErrorHandlerFunction<AnyApiError>[] | RequestInit,
    initOverrides?: RequestInit,
  ): Promise<LatestApiCallResult<R>> {
    const { requestId, controller } = beginLatestRequest();
    const errorHandlersFromCall = Array.isArray(errorHandlers) ? errorHandlers : [];
    const requestInitOverrides = Array.isArray(errorHandlers) ? initOverrides : errorHandlers;

    const result = await request(
      requestParams,
      [ignoreCancellationErrorHandler, ...errorHandlersFromCall],
      mergeSignalIntoInitOverrides(controller.signal, requestInitOverrides),
    );

    if (!isLatestRequest(requestId)) {
      return {
        result: undefined,
        success: false,
      };
    }

    return result;
  }

  return {
    call,
    cancel: abortLatestRequest,
  };
}
